import React from "react";

import { Box, Container, Divider, Grid, List, ListItem, ListItemText, ListSubheader, Typography } from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import SimpleTooltip from "components/SimpleTooltip";
import { SimpleTooltipPlacement } from "components/SimpleTooltip";

import Spacer from "components/Spacer";

import { ProductChapter, selectCurrentChapter, selectSelectedPartner, selectSelectedOperator, setCurrentChapter } from "modules/customer/tools/product/productSlice";
import { useAppDispatch, useAppSelector } from "store";
import Partners from "pages/customer/tools/product/filters/partners/Partners";
import StoreRange from "pages/customer/tools/product/filters/storeProduct/StoreRange";
import stringUtils from "utils/stringUtils";

interface NavigationProps {
    isSetupComplete: boolean
}

const MAX_PARTNER_NAME_LENGTH = 10;
const MAX_OPERATOR_NAME_LENGTH = 10;

const Navigation: React.FC<NavigationProps> = (props: NavigationProps) => {
    const { isSetupComplete } = props;
    const dispatch = useAppDispatch();
    const theme = useTheme();
    const currentChapter = useAppSelector(selectCurrentChapter);
    const selectedPartner = useAppSelector(selectSelectedPartner);
    const selectedOperator = useAppSelector(selectSelectedOperator);
    const truncatedPartnerName = stringUtils.truncate(selectedPartner?.name ?? "", MAX_PARTNER_NAME_LENGTH);
    const truncatedOperatorName = stringUtils.truncate(selectedOperator?.name ?? "", MAX_OPERATOR_NAME_LENGTH);

    const chooseChapter = (newChapter: ProductChapter) => {
        dispatch(setCurrentChapter(newChapter));
    };

    return (
        // @ts-ignore
        <Box bgcolor={theme.palette.background.insight} position="fixed" width={theme.spacing(45)} height="100vh">
            <Container maxWidth={false}>
                <br />
                <Grid container>
                    <Grid md="auto">
                        <SimpleTooltip title={selectedPartner?.name ?? ""} placement={SimpleTooltipPlacement.RightEnd}>
                            <Typography variant="subtitle1" component="div" color="textSecondary">
                                {`Partner: ${truncatedPartnerName}`}
                            </Typography>
                        </SimpleTooltip>
                        {(selectedOperator) && (
                            <SimpleTooltip title={selectedOperator?.name ?? ""} placement={SimpleTooltipPlacement.RightEnd}>
                                <Typography variant="subtitle1" component="div" color="textSecondary">
                                    {`Operator: ${truncatedOperatorName}`}
                                </Typography>
                            </SimpleTooltip>
                        )}
                        <Box paddingBottom={1}>
                            <Typography variant="h3" >
                                Product
                            </Typography>
                        </Box>
                    </Grid>
                    <Spacer />
                    <Grid md="auto">
                        <Partners />
                    </Grid>
                    <Grid md={12}>
                        <StoreRange />
                    </Grid>
                </Grid>
                <br />
                <Divider />
                {isSetupComplete && (
                    <Box paddingTop={2} paddingBottom={2}>
                        <List disablePadding>
                            <ListSubheader disableGutters>
                                Store
                            </ListSubheader>
                            <ListItem
                                button
                                selected={currentChapter === ProductChapter.StoreOverview}
                                onClick={() => chooseChapter(ProductChapter.StoreOverview)}
                            >
                                <ListItemText primary="Store overview" />
                            </ListItem>
                            <ListItem
                                button
                                selected={currentChapter === ProductChapter.StoreOpportunities}
                                onClick={() => chooseChapter(ProductChapter.StoreOpportunities)}
                            >
                                <ListItemText primary="Store opportunities" />
                            </ListItem>
                            <ListSubheader disableGutters>
                                Range
                            </ListSubheader>
                            <ListItem
                                button
                                selected={currentChapter === ProductChapter.RangeOverview}
                                onClick={() => chooseChapter(ProductChapter.RangeOverview)}
                            >
                                <ListItemText primary="Range overview" />
                            </ListItem>
                            <ListItem
                                button
                                selected={currentChapter === ProductChapter.RangeOpportunities}
                                onClick={() => chooseChapter(ProductChapter.RangeOpportunities)}
                            >
                                <ListItemText primary="Range opportunities" />
                            </ListItem>
                            <ListSubheader disableGutters>
                                Product store fit
                            </ListSubheader>
                            <ListItem
                                button
                                selected={currentChapter === ProductChapter.ProductStoreFit}
                                onClick={() => chooseChapter(ProductChapter.ProductStoreFit)}
                            >
                                <ListItemText primary="Product store fit" />
                            </ListItem>
                        </List>
                    </Box>
                )
                }
            </Container >
        </Box >
    );
};

export default Navigation;
